import { PlusOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Button,
  Empty,
  Flex,
  FormInstance,
  InputNumber,
  Select,
  Spin,
  Table
} from 'antd'
import { FormListFieldData, TableColumnsType } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useStocksServiceGetInventoryListEndpointKey } from 'src/api/queries'
import {
  InventoryDocumentResponse,
  ReturnDocumentResponse,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import {
  EditableFormItem,
  requiredRules,
  SubmitButtons,
  Text
} from 'src/components'
import { useStyles } from 'src/features/stock/pages/settlements/styles'
import { useDebounce, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  item?: ReturnDocumentResponse
  fields: FormListFieldData[]
  form?: FormInstance
  add: () => void
  remove: (index: number) => void
}

const fieldsToReset = [
  'inventoryItemId',
  'barCode',
  'name',
  'producerName',
  'expirationDate',
  'quantity',
  'unitPrice',
  'priceTotal',
  'currentQuantity'
]

const generateFields = (medicines: { name?: string }[]) =>
  medicines
    .map((_, index: number) =>
      fieldsToReset.map((field) => ['medicine', index, field])
    )
    .flat()

export const ReturnsConsumablesTable = ({
  fields,
  item,
  form,
  add,
  remove
}: Props) => {
  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)
  const [search, setSearch] = useState('')

  const palette = useTheme()

  const { userData } = useUser()
  const debouncedSearch = useDebounce(search)
  const { styles } = useStyles()
  const { t } = useLocales()

  const {
    data: medicinesData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetInventoryListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getInventoryListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        [String(userData?.currentLegalEntityId)]
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: InventoryDocumentResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const selectedItems = form?.getFieldValue('medicine') || []

  const selectedIds = selectedItems.map(
    (item: { inventoryItemId: string }) => item?.inventoryItemId
  )

  const medicines = medicinesData?.pages.flatMap((page) => page.data) || []

  const options = medicines
    .map((item) => ({
      value: item?.id,
      label: `${item?.internationalName?.name} ${item?.doses} (${t('stock_applications.series')}: ${item?.barCode} - ${t('common.producer')}: ${item?.producer?.name} )`,
      item
    }))
    .filter((item) => (item?.item?.currentQuantity ?? 0) > 0)
    .filter((item) => !selectedIds?.includes(item?.value))

  const addConsumable = async () => {
    const medicines = form?.getFieldValue('medicine')

    if (medicines?.length > 0) {
      const fieldsToValidate = generateFields(medicines)

      await form?.validateFields(fieldsToValidate)
    }

    add()
    setEditingIndex(fields.length)
    setIsNewConsumable(true)
  }

  const onSelect = (index: number, value: string) => {
    const data = form?.getFieldValue('medicine')

    const selectedItem = options?.find((item) => item?.value === value)

    if (selectedItem) {
      data[index] = {
        ...data[index],
        inventoryItemId: selectedItem?.value,
        name: `${selectedItem?.item?.internationalName?.name} ${selectedItem?.item?.doses}`,
        barCode: selectedItem?.item?.barCode,
        producerName: selectedItem?.item?.producer?.name,
        expirationDate: selectedItem?.item?.expirationDate,
        unitPrice: selectedItem?.item?.unitPrice,
        currentQuantity: selectedItem?.item?.currentQuantity
      }

      setSearch('')
      form?.setFieldValue('medicine', data)
    }
  }

  const onSave = async () => {
    const medicines = form?.getFieldValue('medicine')

    const fieldsToValidate = generateFields(medicines)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      setIsNewConsumable(false)
      setEditingIndex(undefined)
      form?.setFieldsValue({ medicine: values?.medicine })
    }
  }

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(fieldsToReset.map((field) => ['stock', index, field]))
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const columns: TableColumnsType = [
    {
      key: 'barCode',
      minWidth: 150,
      title: t('common.code'),
      render: (_, __, index) => {
        const code = form?.getFieldValue(['medicine', index, 'barCode'])

        return code ?? '-'
      }
    },
    {
      key: 'name',
      width: '25%',
      title: t('common.commercial_naming'),
      render: (_, __, index) => {
        return item ? (
          <Text weight={400} color={palette.blue6}>
            {form?.getFieldValue(['medicine', index, 'name'])}
          </Text>
        ) : (
          <EditableFormItem
            name={[index, 'name']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <Select
              allowClear
              showSearch
              popupMatchSelectWidth={false}
              filterOption={false}
              onSearch={(value) => setSearch(value)}
              onClear={() => setSearch('')}
              loading={isFetchingMedicines}
              options={options}
              onSelect={(value) => onSelect(index, value)}
              placeholder={t('common.active_component')}
              onPopupScroll={(e) => {
                const target = e.target as HTMLElement
                if (
                  target.scrollTop + target.offsetHeight ===
                  target.scrollHeight
                )
                  hasNextMedicinesPage && fetchNextMedicinesPage()
              }}
              notFoundContent={
                isFetchingMedicines ? (
                  <Flex justify="center">
                    <Spin spinning />
                  </Flex>
                ) : (
                  <Empty />
                )
              }
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'producerName',
      width: 150,
      title: t('common.producer'),
      render: (_, __, index) => {
        return (
          <Text
            weight={400}
            color={palette.cyan7}
            ellipsis
            className={styles.producerText}
          >
            {form?.getFieldValue(['medicine', index, 'producerName'])}
          </Text>
        )
      }
    },
    {
      key: 'expirationDate',
      minWidth: 150,
      title: t('common.expiration_date'),
      render: (_, __, index) => {
        const expirationDate = form?.getFieldValue([
          'medicine',
          index,
          'expirationDate'
        ])

        return expirationDate
          ? dayjs(expirationDate).format(dayMonthYearBackSlash)
          : '-'
      }
    },
    ...(!item
      ? [
          {
            key: 'currentQuantity',
            minWidth: 150,
            title: t('stock_write_offs.current_quantity'),
            render: (_: unknown, __: unknown, index: number) => {
              const currentQuantity = form?.getFieldValue([
                'medicine',
                index,
                'currentQuantity'
              ])

              return currentQuantity ?? '-'
            }
          }
        ]
      : []),
    {
      key: 'quantity',
      minWidth: 200,
      title: t('medicine_register.consumables.unit_count'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'quantity']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <InputNumber
              min={0}
              placeholder={t('common.quantity')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'unitPrice',
      minWidth: 120,
      title: t('stock_administration.price_unit'),
      render: (_, __, index) => {
        const unitPrice = form?.getFieldValue(['medicine', index, 'unitPrice'])

        return unitPrice ?? '-'
      }
    },
    {
      key: 'priceTotal',
      minWidth: 120,
      title: t('stock_administration.price_total'),
      render: (_, __, index) => {
        const total = form?.getFieldValue(['medicine', index, 'priceTotal'])

        return total !== undefined ? total : '-'
      }
    },
    ...(!item
      ? [
          {
            width: 105,
            fixed: 'right' as const,
            key: 'actions',
            render: (_: unknown, __: unknown, index: number) => (
              <SubmitButtons
                editingIndex={editingIndex}
                isEditing={editingIndex === index}
                onSave={onSave}
                onCancel={() => onCancel(index)}
                onEdit={() => setEditingIndex(index)}
                onDelete={() => remove(index)}
              />
            )
          }
        ]
      : [])
  ]

  return (
    <Table
      className={styles.table}
      dataSource={fields}
      bordered
      pagination={false}
      scroll={{ x: 'max-content' }}
      columns={columns}
      footer={() =>
        !item && (
          <Flex justify="center">
            <Button onClick={addConsumable} icon={<PlusOutlined />}>
              {t('analysis.add_test.add_consumable')}
            </Button>
          </Flex>
        )
      }
    />
  )
}
